/**
 * WisetapProvider — the in-house ingest sink.
 *
 * Events are batched through EventQueue (persistent, AsyncStorage-backed via
 * @lib/storage) and POSTed to /v1/oa/events. Profiles go straight to
 * /v1/oa/user/profile. Device context + session are initialised on construction.
 */
import { AppState } from "react-native";
import { AnalyticsProvider } from "../AnalyticsProvider";
import { EventQueue } from "../wisetap/EventQueue";
import {
  sendEvents,
  postProfile,
  isIngestEnabled,
} from "../wisetap/ingestClient";
import { toEvent, toProfile, stampIngestedAt } from "../wisetap/mappers";
import { snapshot, initDeviceContext } from "../wisetap/context";
import {
  getSessionId,
  hydrateSession,
  resetSession,
  touchSession,
} from "../wisetap/session";
import type { EventProps, Traits } from "../wisetap/types";

export class WisetapProvider extends AnalyticsProvider {
  private readonly queue: EventQueue;
  private readonly sub: ReturnType<typeof AppState.addEventListener>;

  constructor() {
    super();
    this.queue = new EventQueue((batch) => sendEvents(stampIngestedAt(batch)));
    initDeviceContext();
    hydrateSession();
    this.sub = AppState.addEventListener("change", (state) => {
      if (state === "active") {
        touchSession();
      } else {
        this.flush(); // drain the buffer before the OS can kill us
      }
    });
  }

  track(eventName: string, props?: EventProps): void {
    if (!eventName || !isIngestEnabled()) return;
    try {
      this.queue.enqueue(toEvent(eventName, props, snapshot(), getSessionId()));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log("wisetap track err", e);
    }
  }

  trackCharged(details?: EventProps, items?: unknown): void {
    // No dedicated charged endpoint — flatten into a normal event.
    const name =
      typeof details?.event_name === "string"
        ? details.event_name
        : "Order Complete";
    this.track(name, { ...details, cart_items: items });
  }

  identify(userId: string | number, traits?: Traits): void {
    if (!isIngestEnabled()) return;
    try {
      postProfile(toProfile(userId, traits, snapshot()));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log("wisetap identify err", e);
    }
  }

  reset(): void {
    this.flush();
    resetSession();
  }

  flush(): void {
    if (!isIngestEnabled()) return;
    this.queue.flush();
    touchSession();
  }
}

export default WisetapProvider;
